import { useAction, useAtom } from '@reatom/npm-react'
import { useEffect, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import styled, { css } from 'styled-components'

import { Array, AsyncStateN } from '@blade/std-lib'
import { CurrencyListItemSkeleton } from '@root/entities/currency/ui'
import {
  InfoMessage,
  InputDeprecated as Input,
  ScrollContainer,
  SearchIcon,
} from '@root/shared/ui-kit'

import { currencySelectorReatom } from '../../atoms'
import { CurrencySelectorItem } from '../currency-selector-item'

import type { CurrencySet } from '../../atoms'
import type { CurrencyAddress, CurrencyModel } from '@root/entities/currency'
import type { WalletModel } from '@root/entities/wallet/atoms'
import type { ClassNameProps } from '@root/shared/react'

const placeholders = Array.range(0, 6)

export type CurrencySelectorListProps = {
  wallet: WalletModel
  onCurrencySelect?: (currency: CurrencyModel) => void
  exclude?: Array<CurrencyAddress>
  fixedCurrencies?: Array<CurrencyModel>
  sets?: Array<CurrencySet>
  iconSize?: number
} & ClassNameProps

export const CurrencySelectorList: React.FC<CurrencySelectorListProps> = ({
  className,
  wallet,
  onCurrencySelect,
  exclude = [],
  sets = [],
  fixedCurrencies = [],
}) => {
  const model = useMemo(
    () =>
      currencySelectorReatom({
        exclude: exclude,
        sets: sets,
        walletId: wallet.id,
        currencies: fixedCurrencies,
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  )
  const [currencies] = useAtom(model.currenciesAtom)
  const [searchQuery] = useAtom(model.searchQueryAtom)
  const [searchState] = useAtom(model.searchCurrencyStateAtom)
  const [isLoading] = useAtom(model.isLoadingAtom)

  const initialize = useAction(model.initializeAction)
  const setQuery = useAction(model.setQueryAction)

  const { t } = useTranslation()

  useEffect(() => {
    initialize()
  }, [initialize])

  const renderSkeletons = () => {
    if (isLoading) {
      return placeholders.map((it) => (
        <CurrencyListItemSkeleton key={`skeleton-${it}`} />
      ))
    }
  }

  const renderContent = () => {
    if (AsyncStateN.isNotInitiated(searchState)) {
      if (searchQuery && currencies.length === 0) {
        return <InfoMessage intent="empty" />
      }

      return (
        <ScrollContainer>
          <List>
            {currencies.map((currency) => (
              <CurrencySelectorItem
                key={currency.currency.address}
                currency={currency}
                onCurrencySelect={onCurrencySelect}
              />
            ))}
            {renderSkeletons()}
          </List>
        </ScrollContainer>
      )
    }

    if (AsyncStateN.isLoading(searchState)) {
      return (
        <ScrollContainer>
          <CurrencyListItemSkeleton />
        </ScrollContainer>
      )
    }
    if (AsyncStateN.isSuccess(searchState)) {
      const item = searchState.value
      return (
        <ScrollContainer>
          <List>
            <CurrencySelectorItem
              currency={item}
              onCurrencySelect={onCurrencySelect}
            />
          </List>
        </ScrollContainer>
      )
    }

    return <InfoMessage intent="error" />
  }

  return (
    <Root className={className}>
      <InputContainer>
        <Input
          css={css`
            background: var(--background-3);
          `}
          value={searchQuery}
          onChange={(e) => setQuery(e.target.value)}
          startIcon={SearchIcon}
          placeholder={t('currency.search_token_or_address')}
        />
      </InputContainer>
      {renderContent()}
    </Root>
  )
}

const Root = styled.div`
  display: grid;
  grid-template-rows: auto minmax(0, 1fr);
`

const InputContainer = styled.div`
  padding-inline: 12px;
  margin-bottom: 12px;
`

const List = styled.div`
  display: grid;
  padding-block: 0 var(--safe-bottom);
`
